import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { FaMap, FaStar, FaQuoteLeft, FaChevronLeft, FaChevronRight, FaRocket, FaGraduationCap, FaUsers, FaChartLine, FaCheckCircle, FaArrowRight, FaCodeBranch, FaBook, FaComments } from "react-icons/fa";
import { SiChatbot } from "react-icons/si";
import { TbReportAnalytics, TbTargetArrow, TbBulb, TbRocket, TbBrain, TbTrendingUp } from "react-icons/tb";
import { HiAcademicCap, HiLightningBolt, HiSparkles } from "react-icons/hi";
import { useAuth } from '../context/AuthContext';

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  
  // Sample testimonials data
  const testimonials = [
     {
      name: 'Smita Kulkarni',
      role: 'UX Designer',
      content: 'Mentor support was exceptional. My mentor helped me build a strong portfolio that got me multiple job offers.',
      rating: 5,
      avatar: 'SK',
      company: ''
    },
    {
      name: 'Samarth Jadhav',
      role: 'Software Engineer',
      content: 'Gaplify helped me identify the exact skills I needed to transition into tech. The personalized roadmap made all the difference in my career change journey.',
      rating: 5,
      avatar: 'SJ',
      company: ''
    },
    {
      name: 'Rohan Shrivastava',
      role: 'Product Manager',
      content: 'The career assistant provided insights I never would have considered. It helped me position myself for a promotion within just 6 months!',
      rating: 5,
      avatar: 'RS',
      company: 'InfoTech.co'
    },
    {
      name: 'Anjali Patil',
      role: 'Data Scientist',
      content: 'The skill gap analysis was eye-opening. I was able to focus my learning on high-impact areas that directly contributed to landing my dream job.',
      rating: 5,
      avatar: 'AP',
      company: 'A2Z Analytics'
    }
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach(el => observer.observe(el));

    return () => {
      elements.forEach(el => observer.unobserve(el));
    };
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const navigateTo = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6] overflow-x-hidden" style={{
        backgroundImage: `
          radial-gradient(at 0% 0%, rgba(59, 130, 246, 0.15) 0px, transparent 50%),
          radial-gradient(at 100% 0%, rgba(139, 92, 246, 0.15) 0px, transparent 50%),
          radial-gradient(at 100% 100%, rgba(236, 72, 153, 0.15) 0px, transparent 50%),
          radial-gradient(at 0% 100%, rgba(34, 197, 94, 0.15) 0px, transparent 50%)
        `
      }}>
      
      {/* Top Decoration */}
      <div className="h-[45rem] bg-gradient-to-r from-blue-600 to-indigo-700 w-full absolute top-0 z-0 rounded-b-[3rem] shadow-lg">
         <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="container-responsive relative z-10">
          <div className="text-center max-w-5xl mx-auto">
            <div className="animate-fade-in-up">
              
              <h1 className="heading-primary mb-8 text-white">
                Bridge the Gap to Your <br/>
                <span className="text-transparent  bg-clip-text bg-gradient-to-r from-[#FFD166] to-[#F4A261]">Dream Career</span>
              </h1>
              
              <p className="text-xl  md:text-2xl mb-12 text-blue-100 max-w-3xl mx-auto leading-relaxed font-medium">
                 Your comprehensive career guidance platform that bridges the gap between your skills and dream career with personalized learning paths and real-time insights.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
                {!user ? (
                  <button 
                    onClick={() => navigateTo('/signup')}
                    className="group relative px-8 py-4 bg-white text-indigo-900 font-bold rounded-2xl shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all overflow-hidden"
                  >
                    <span className="relative flex items-center text-lg">
                       <FaRocket className="w-5 h-5 mr-3 group-hover:animate-bounce" />
                       Get Started Free
                    </span>
                  </button>
                ) : (
                  <button 
                    onClick={() => navigateTo('/skills-analysis')}
                    className="group px-8 py-4 bg-white text-indigo-600 font-bold rounded-2xl shadow-lg border border-indigo-100 hover:border-indigo-200 hover:shadow-xl hover:-translate-y-1 transition-all flex items-center text-lg"
                  >
                    <TbTargetArrow className="w-6 h-6 mr-3 text-indigo-600" />
                    Analyze My Skills
                  </button>
                )}
                <button 
                    onClick={() => navigateTo('/mentors')}
                    className="px-8 py-4 bg-white/10 text-white font-bold rounded-2xl border-2 border-white/20 hover:bg-white/20 hover:border-white transition-all flex items-center text-lg backdrop-blur-sm"
                >
                    Find a Mentor <FaArrowRight className="ml-2 w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative Floating Elements */}
        <div className="absolute top-32 left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-32 right-10 w-48 h-48 bg-purple-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-20 w-24 h-24 bg-pink-500/30 rounded-full blur-3xl animate-pulse delay-700"></div>
      </section>

      {/* Our Mission Section */}
      <section className="py-24 px-4">
        <div className="container-responsive">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-1.5 bg-blue-100/50 text-blue-700 rounded-full text-xs font-bold uppercase tracking-wider mb-6 border border-blue-200">
              <HiAcademicCap className="w-4 h-4 mr-2" />
              Our Mission
            </div>
            <h2 className="heading-primary mb-6 text-gray-900">
              Empowering Your <span className="text-blue-600">Career Journey</span>
            </h2>
            <div className="w-24 h-1 bg-gray-200 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              At Gaplify, we're committed to empowering individuals with data-driven career guidance, personalized learning paths, and real-time skill analysis to help you thrive in today's competitive job market.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
                { icon: TbTargetArrow, title: "Clear Direction", desc: "Find your ideal career path with our comprehensive assessment tools.", color: "text-red-600", bg: "bg-red-50" },
                { icon: TbBulb, title: "Smart Learning", desc: "Personalized learning recommendations based on your goals, current skills, and market demands.", color: "text-amber-600", bg: "bg-amber-50" },
                { icon: TbRocket, title: "Career Growth", desc: "Stay ahead with real-time industry insights, skill trends, and expert mentorship support.", color: "text-emerald-600", bg: "bg-emerald-50" }
            ].map((feature, idx) => (
                <div key={idx} className="bg-white/50 backdrop-blur-lg p-8 rounded-[2rem] border border-white/60 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 group">
                    <div className={`w-16 h-16 ${feature.bg} ${feature.color} rounded-2xl flex items-center justify-center text-3xl mb-6 group-hover:rotate-6 transition-transform shadow-sm`}>
                        <feature.icon />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed font-medium">{feature.desc}</p>
                </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services - Expanded with Open Source & Resource Hub */}
      <section className="py-24 px-4 bg-white/40 backdrop-blur-sm">
        <div className="container-responsive">
          <div className="text-center mb-20">
             <div className="inline-flex items-center px-4 py-1.5 bg-purple-100/50 text-purple-700 rounded-full text-xs font-bold uppercase tracking-wider mb-6 border border-purple-200">
              <HiLightningBolt className="w-4 h-4 mr-2" />
              Our Services
            </div>
            <h2 className="heading-primary mb-6 text-gray-900">
              Comprehensive <span className="text-purple-600">Solutions</span>
            </h2>
             <div className="w-24 h-1 bg-gray-200 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Everything you need to accelerate your career growth and achieve your professional goals.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1: Skill Analysis */}
            <div className="bg-white rounded-[2rem] p-8 shadow-xl border border-gray-100 hover:border-blue-200 hover:shadow-2xl transition-all duration-300 flex flex-col h-full group relative overflow-hidden">
              <div className="w-20 h-20 bg-blue-600 rounded-2xl flex items-center justify-center text-white text-3xl mb-8 shadow-lg shadow-blue-200 z-10 group-hover:scale-110 transition-transform">
                <TbReportAnalytics />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4 z-10">Skill Analysis</h3>
              <p className="text-gray-600 mb-8 font-medium z-10">Get detailed insights about your current skills and how they match market demands.</p>
              <div className="mt-auto">
                 <button onClick={() => navigateTo('/skills-analysis')} className="w-full py-4 rounded-xl bg-blue-50 text-blue-700 font-bold hover:bg-blue-600 hover:text-white transition-all z-10">
                    Analyze Skills
                </button>
              </div>
            </div>

            {/* Card 2: Career Roadmaps */}
            <div className="bg-white rounded-[2rem] p-8 shadow-xl border border-gray-100 hover:border-purple-200 hover:shadow-2xl transition-all duration-300 flex flex-col h-full group relative overflow-hidden">
              <div className="w-20 h-20 bg-purple-600 rounded-2xl flex items-center justify-center text-white text-3xl mb-8 shadow-lg shadow-purple-200 z-10 group-hover:scale-110 transition-transform">
                <FaMap />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4 z-10">Career Roadmaps</h3>
              <p className="text-gray-600 mb-8 font-medium z-10">Personalized learning paths tailored to your career aspirations and goals.</p>
               <div className="mt-auto">
                <button onClick={() => navigateTo('/roadmap')} className="w-full py-4 rounded-xl bg-purple-50 text-purple-700 font-bold hover:bg-purple-600 hover:text-white transition-all z-10">
                    Explore Roadmaps
                </button>
              </div>
            </div>

            {/* Card 3: Expert Mentors */}
            <div className="bg-white rounded-[2rem] p-8 shadow-xl border border-gray-100 hover:border-pink-200 hover:shadow-2xl transition-all duration-300 flex flex-col h-full group relative overflow-hidden">
              <div className="w-20 h-20 bg-pink-600 rounded-2xl flex items-center justify-center text-white text-3xl mb-8 shadow-lg shadow-pink-200 z-10 group-hover:scale-110 transition-transform">
                <FaUsers />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4 z-10">Expert Mentors</h3>
              <p className="text-gray-600 mb-8 font-medium z-10">Connect with industry professionals for personalized career guidance and growth.</p>
               <div className="mt-auto">
                <button onClick={() => navigateTo('/mentors')} className="w-full py-4 rounded-xl bg-pink-50 text-pink-700 font-bold hover:bg-pink-600 hover:text-white transition-all z-10">
                    Find a Mentor
                </button>
              </div>
            </div>

            {/* Card 4: Open Source Projects (NEW) */}
            <div className="bg-white rounded-[2rem] p-8 shadow-xl border border-gray-100 hover:border-emerald-200 hover:shadow-2xl transition-all duration-300 flex flex-col h-full group relative overflow-hidden">
              <div className="w-20 h-20 bg-emerald-600 rounded-2xl flex items-center justify-center text-white text-3xl mb-8 shadow-lg shadow-emerald-200 z-10 group-hover:scale-110 transition-transform">
                <FaCodeBranch />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4 z-10">Open Source</h3>
              <p className="text-gray-600 mb-8 font-medium z-10">Contribute to real-world projects, build your portfolio, and gain practical experience.</p>
               <div className="mt-auto">
                <button onClick={() => navigateTo('/open-source')} className="w-full py-4 rounded-xl bg-emerald-50 text-emerald-700 font-bold hover:bg-emerald-600 hover:text-white transition-all z-10">
                    Browse Projects
                </button>
              </div>
            </div>

             {/* Card 5: Resource Hub (NEW) */}
             <div className="bg-white rounded-[2rem] p-8 shadow-xl border border-gray-100 hover:border-amber-200 hover:shadow-2xl transition-all duration-300 flex flex-col h-full group relative overflow-hidden">
              <div className="w-20 h-20 bg-amber-500 rounded-2xl flex items-center justify-center text-white text-3xl mb-8 shadow-lg shadow-amber-200 z-10 group-hover:scale-110 transition-transform">
                <FaBook />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4 z-10">Resource Hub</h3>
              <p className="text-gray-600 mb-8 font-medium z-10">Access a curated library of top-tier courses, certifications, and learning tools.</p>
               <div className="mt-auto">
                <button onClick={() => navigateTo('/resource-hub')} className="w-full py-4 rounded-xl bg-amber-50 text-amber-700 font-bold hover:bg-amber-500 hover:text-white transition-all z-10">
                    Visit Library
                </button>
              </div>
            </div>

             {/* Card 6: Community (NEW) */}
             <div className="bg-white rounded-[2rem] p-8 shadow-xl border border-gray-100 hover:border-cyan-200 hover:shadow-2xl transition-all duration-300 flex flex-col h-full group relative overflow-hidden">
              <div className="w-20 h-20 bg-cyan-600 rounded-2xl flex items-center justify-center text-white text-3xl mb-8 shadow-lg shadow-cyan-200 z-10 group-hover:scale-110 transition-transform">
                <FaComments />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4 z-10">Community</h3>
              <p className="text-gray-600 mb-8 font-medium z-10">Join our vibrant community of learners, share knowledge, and grow together.</p>
               <div className="mt-auto">
                <button onClick={() => navigateTo('/community')} className="w-full py-4 rounded-xl bg-cyan-50 text-cyan-700 font-bold hover:bg-cyan-600 hover:text-white transition-all z-10">
                    Join Discussion
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 px-4 overflow-hidden">
        <div className="container-responsive">
          <div className="text-center mb-14 md:mb-20">
             <div className="inline-flex items-center px-4 py-1.5 bg-yellow-100/50 text-yellow-700 rounded-full text-xs font-bold uppercase tracking-wider mb-6 border border-yellow-200">
              <FaStar className="w-4 h-4 mr-2" />
              Success Stories
            </div>
            <h2 className="heading-primary mb-6 text-gray-900">
              What Our Users <span className="text-yellow-500">Say</span>
            </h2>
            <div className="w-24 h-1 bg-gray-200 mx-auto mb-6 rounded-full"></div>
            <p className="text-lg md:text-xl text-gray-600">Join thousands of professionals who have transformed their careers with Gaplify</p>
          </div>
          
          <div className="relative max-w-5xl mx-auto">
            <div className="bg-white/70 backdrop-blur-2xl rounded-[2rem] md:rounded-[3rem] p-6 md:p-12 shadow-2xl border border-white/60 relative">
               <FaQuoteLeft className="absolute top-6 left-6 md:top-10 md:left-10 text-4xl md:text-6xl text-gray-100 z-0" />
               
               <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 md:gap-10">
                  <div className="w-24 h-24 md:w-48 md:h-48 flex-shrink-0">
                      <div className="w-full h-full rounded-2xl md:rounded-3xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-2xl md:text-4xl font-bold shadow-2xl rotate-3">
                          {testimonials[currentTestimonial].avatar}
                      </div>
                  </div>
                  
                  <div className="flex-1 text-center md:text-left">
                      <div className="flex justify-center md:justify-start gap-1 mb-4 md:mb-6">
                          {[...Array(5)].map((_, i) => (
                             <FaStar key={i} className={`w-5 h-5 md:w-6 md:h-6 ${i < testimonials[currentTestimonial].rating ? 'text-yellow-400' : 'text-gray-200'}`} />
                          ))}
                      </div>
                      
                      <p className="text-lg md:text-3xl text-gray-800 font-medium leading-relaxed mb-6">
                          "{testimonials[currentTestimonial].content}"
                      </p>
                      
                       <div>
                          <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">{testimonials[currentTestimonial].name}</h4>
                          <p className="text-indigo-600 font-bold text-sm md:text-base">{testimonials[currentTestimonial].role}</p>
                       </div>
                  </div>
               </div>
               
               {/* Navigation Buttons */}
               <div className="flex justify-center md:justify-end gap-3 mt-8 md:mt-0 md:absolute md:bottom-12 md:right-12">
                   <button onClick={prevTestimonial} className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center text-gray-600 hover:text-indigo-600 hover:scale-110 transition-all">
                       <FaChevronLeft className="w-3 h-3 md:w-4 md:h-4" />
                   </button>
                   <button onClick={nextTestimonial} className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-indigo-600 shadow-lg shadow-indigo-300 flex items-center justify-center text-white hover:bg-indigo-700 hover:scale-110 transition-all">
                       <FaChevronRight className="w-3 h-3 md:w-4 md:h-4" />
                   </button>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4">
        <div className="container-responsive">
            <div className="relative rounded-[2rem] md:rounded-[3rem] overflow-hidden p-8 md:p-24 text-center bg-gray-900 shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-900 to-gray-900 opacity-80"></div>
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                
                <div className="relative z-10 max-w-4xl mx-auto">
                    <h2 className="heading-primary text-white mb-6 md:mb-8 text-3xl md:text-5xl">Ready to Transform Your Career?</h2>
                    <p className="text-lg md:text-xl text-blue-100 mb-10 md:mb-12 max-w-2xl mx-auto">Join thousands of professionals who have already accelerated their careers with Gaplify</p>
                    
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        {!user ? (
                            <button 
                                onClick={() => navigateTo('/signup')}
                                className="px-8 py-4 md:px-10 md:py-5 bg-white text-indigo-900 text-base md:text-lg font-bold rounded-2xl hover:scale-105 transition-transform shadow-xl flex items-center justify-center"
                            >
                                <FaRocket className="mr-3" /> Get Started Free
                            </button>
                        ) : (
                             <button 
                                onClick={() => navigateTo('/mentors')}
                                className="px-8 py-4 md:px-10 md:py-5 bg-white text-indigo-900 text-base md:text-lg font-bold rounded-2xl hover:scale-105 transition-transform shadow-xl flex items-center justify-center"
                            >
                                Schedule a Demo
                            </button>
                        )}
                         <button 
                            onClick={() => navigateTo('/about')}
                            className="px-8 py-4 md:px-10 md:py-5 bg-transparent border-2 border-white/20 text-white text-base md:text-lg font-bold rounded-2xl hover:bg-white/10 transition-all"
                        >
                            Learn More
                        </button>
                    </div>
                </div>
                
                 {/* Floating Shapes */}
                 <div className="absolute top-10 left-10 w-20 h-20 bg-blue-500 rounded-full mix-blend-overlay filter blur-xl opacity-30 animate-blob"></div>
                 <div className="absolute bottom-10 right-10 w-32 h-32 bg-purple-500 rounded-full mix-blend-overlay filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
            </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
